/**
 * @fileoverview `<SectionHeader>` — titre compact de section opérationnelle.
 *
 * Utilisé à l'intérieur des pages backoffice pour séparer les panneaux sans
 * transformer chaque section en carte décorative. Les actions restent dans un
 * slot explicite pour conserver des surfaces de clic cohérentes.
 */

import type { ReactNode } from 'react';
import { cn } from '../lib/utils.ts';

export interface SectionHeaderProps {
  /** Titre de section. */
  title: ReactNode;
  /** Texte d'aide optionnel sous le titre. */
  description?: ReactNode;
  /** Badge, bouton, menu ou autre action courte. */
  action?: ReactNode;
  /** Classe CSS complémentaire. */
  className?: string;
}

/**
 * En-tête de section.
 *
 * @param props - Titre, description, action et classe CSS.
 * @returns Bloc compact adapté aux panneaux de dashboard et listes.
 *
 * @example
 * ```tsx
 * <SectionHeader title="Anomalies STI" action={<Button size="sm">Voir tout</Button>} />
 * ```
 */
export function SectionHeader({
  title,
  description,
  action,
  className,
}: SectionHeaderProps): React.JSX.Element {
  return (
    <div className={cn('flex items-start justify-between gap-3', className)}>
      <div className="min-w-0 space-y-1">
        <h2 className="text-quipux-navy-950 text-base font-semibold">{title}</h2>
        {description ? <p className="text-muted-foreground text-sm leading-5">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
